import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

import { ButtonComponent } from '@shared/components/buttons/button/button.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { LoaderComponent } from '@shared/components/loaders/loader/loader.component';
import { LoaderSmallComponent } from '@shared/components/loaders/loader-small/loader-small.component';
import { LocalizeTextPipe } from '@shared/pipes/localize-text/localize-text.pipe';
import { HoverTooltipCardDirective } from '@shared/directives/hover-tooltip-card.directive';

interface Directory {
  id: string;
  path: string;      // ltree, e.g. "patients.123.private"
  name: string;
  parentPath: string;
  updatedAt?: number;
  isShared?: boolean;
  shareExpiresAt?: number;
  shareRecipientEmail?: string;
}
interface Document {
  id: string;
  dirPath: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  updatedAt: number;
}
type Row =
  | { kind: 'dir'; id: string; name: string; path: string; updatedAt?: number; isShared?: boolean; shareExpiresAt?: number; shareRecipientEmail?: string }
  | { kind: 'file'; id: string; name: string; mime: string; size: number; updatedAt: number };

type DirRow = Extract<Row, { kind: 'dir' }>;
type BreadcrumbItem = { label: string; path: string; sourceIndex: number };

@Component({
  selector: 'app-documents',
  templateUrl: './documents.component.html',
  imports: [ButtonComponent, IconComponent, LoaderComponent, LoaderSmallComponent, LocalizeTextPipe, HoverTooltipCardDirective],
  standalone: true,
})
export class DocumentsComponent implements OnChanges {
  // ----- Inputs driven by parent -----
  @Input() currentPath = '';
  @Input() subdirs: Directory[] = [];
  @Input() docs: Document[] = [];
  @Input() rootPath = '';

  /** Separate loaders */
  @Input() loadingFiles = false;
  @Input() loadingUpload = false;
  @Input() loadingExport = false;

  @Input() dirFilter: string | null = '';
  @Input() fileOnly = false;
  @Input() allowDelete = false;
  @Input() uploadsDisabled = false;
  @Input() activeCategory: 'all' | 'referrals' = 'all';

  // ----- Outputs to parent -----
  @Output() navigate = new EventEmitter<string>();
  @Output() filterChange = new EventEmitter<string>();
  @Output() downloadRequested = new EventEmitter<string>();
  @Output() previewRequested = new EventEmitter<string>();
  @Output() uploadRequested = new EventEmitter<{ file: File; dirPath: string }>();
  @Output() createFolderRequested = new EventEmitter<{ name: string; parentPath: string }>();
  @Output() renameRequested = new EventEmitter<{ documentId: string; newFilename: string }>();
  @Output() renameDirectoryRequested = new EventEmitter<{ path: string; newName: string }>();
  @Output() moveDocumentRequested = new EventEmitter<{ documentId: string; dirPath: string }>();
  @Output() deleteRequested = new EventEmitter<string>();
  @Output() shareFolderRequested = new EventEmitter<{ id: string; name: string; path: string }>();
  @Output() ingestDocumentRequested = new EventEmitter<any>();
  @Output() exportZipRequested = new EventEmitter<{ documentIds: string[]; zipName?: string }>();
  @Output() refreshRequested = new EventEmitter<void>();
  @Input() uploadingFileCount = 0;

  breadcrumbs: string[] = [];
  breadcrumbDisplayData: BreadcrumbItem[] = [];
  rows: Row[] = [];
  selectedDocIds = new Set<string>();
  lastUploadMsg = '';
  isDragActive = false;
  dragCounter = 0;
  pendingUploads: Array<{ name: string; size: number }> = [];
  isCreatingFolder = false;
  newFolderName = '';
  activeDropDirId: string | null = null;
  draggedDocumentId: string | null = null;
  pendingDropDir: DirRow | null = null;
  activeBreadcrumbIndex: number | null = null;
  pendingBreadcrumbPath: string | null = null;
  renamingRowId: string | null = null;
  renamingRowKind: 'file' | 'dir' | null = null;
  renamingDirPath: string | null = null;
  renameDraft = '';
  actionsMenuOpen = false;

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('newFolderInput') newFolderInput?: ElementRef<HTMLInputElement>;
  @ViewChild('renameInput') renameInput?: ElementRef<HTMLInputElement>;
  @ViewChild('actionButton', { read: ElementRef }) actionButton?: ElementRef<HTMLElement>;
  @ViewChild('dropdown') dropdown?: ElementRef<HTMLDivElement>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentPath || changes.docs) {
      this.clearSelection();
    }

    this.breadcrumbs = this.currentPath ? this.currentPath.split('.') : [];
    this.breadcrumbDisplayData = this.buildBreadcrumbDisplayData();

    const dirRows: Row[] = (this.subdirs || []).map(d => ({
      kind: 'dir',
      id: d.id,
      name: d.name,
      path: d.path,
      updatedAt: d.updatedAt,
      isShared: d.isShared,
      shareExpiresAt: d.shareExpiresAt,
      shareRecipientEmail: d.shareRecipientEmail,
    }));
    const fileRows: Row[] = (this.docs || []).map(x => ({
      kind: 'file',
      id: x.id,
      name: x.filename,
      mime: x.mimeType,
      size: x.sizeBytes,
      updatedAt: x.updatedAt,
    }));
    this.rows = [...dirRows, ...fileRows];

    if (changes.loadingUpload && !this.loadingUpload) {
      this.clearPendingUploads();
    }
  }

  get isAllSelected(): boolean {
    const fileIds = (this.docs || []).map(d => d.id);
    return fileIds.length > 0 && fileIds.every(id => this.selectedDocIds.has(id));
  }

  get isSomeSelected(): boolean {
    return this.selectedDocIds.size > 0 && !this.isAllSelected;
  }

  get selectedCount(): number {
    return this.selectedDocIds.size;
  }

  toggleSelectAll(checked: boolean): void {
    if (checked) {
      (this.docs || []).forEach(d => this.selectedDocIds.add(d.id));
    } else {
      this.selectedDocIds.clear();
    }
  }

  toggleDocSelection(docId: string, event: Event): void {
    event.stopPropagation();
    if (this.selectedDocIds.has(docId)) {
      this.selectedDocIds.delete(docId);
    } else {
      this.selectedDocIds.add(docId);
    }
  }

  clearSelection(): void {
    this.selectedDocIds.clear();
  }

  exportSelected(): void {
    if (this.selectedDocIds.size === 0) return;
    this.exportZipRequested.emit({
      documentIds: Array.from(this.selectedDocIds),
    });
  }

  exportAll(): void {
    if (!this.docs || this.docs.length === 0) return;
    this.exportZipRequested.emit({
      documentIds: this.docs.map(d => d.id),
    });
  }

  fmtShareExpiry(expiryMs?: number): string {
    if (!expiryMs) return '';
    const date = new Date(expiryMs < 1e11 ? expiryMs * 1000 : expiryMs);
    return date.toLocaleString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  getShareTooltipLines(r: Row): string[] {
    const lines: string[] = [];
    if (this.isDir(r)) {
      if (r.shareRecipientEmail) {
        lines.push(`Recipient: ${r.shareRecipientEmail}`);
      }
      if (r.shareExpiresAt) {
        lines.push(`Expires: ${this.fmtShareExpiry(r.shareExpiresAt)}`);
      }
    }
    return lines;
  }

  getDocById(id: string): Document | null {
    return (this.docs || []).find(d => d.id === id) || null;
  }

  // handlers (emit only)
  onFilterInput(ev: Event) {
    const v = (ev.target as HTMLInputElement).value;
    this.filterChange.emit(v);
  }

  openRow(r: Row) {
    if (this.isDir(r)) {
      this.navigate.emit(r.path);
      return;
    }
    this.previewRequested.emit(r.id);
  }

  download(id: string) {
    this.downloadRequested.emit(id);
  }

  delete(id: string) {
    this.deleteRequested.emit(id);
  }

  triggerUpload() {
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(ev: Event) {
    const files = (ev.target as HTMLInputElement).files;
    if (!files || files.length === 0) return;
    this.queueFiles(Array.from(files));
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  openCreateFolderInput() {
    this.isCreatingFolder = true;
    this.newFolderName = '';
    setTimeout(() => this.newFolderInput?.nativeElement.focus());
  }

  cancelCreateFolder() {
    this.isCreatingFolder = false;
    this.newFolderName = '';
  }

  onNewFolderInput(ev: Event) {
    this.newFolderName = (ev.target as HTMLInputElement).value;
  }

  confirmCreateFolder() {
    this.submitCreateFolder();
  }

  submitCreateFolder() {
    const name = this.newFolderName.trim();
    if (!name) {
      this.cancelCreateFolder();
      return;
    }
    this.createFolderRequested.emit({
      name,
      parentPath: this.currentPath || this.rootPath,
    });
    this.isCreatingFolder = false;
    this.newFolderName = '';
  }

  startRenaming(r: Row) {
    this.renamingRowId = r.id;
    this.renamingRowKind = r.kind;
    this.renamingDirPath = r.kind === 'dir' ? r.path : null;
    this.renameDraft = r.name;
    setTimeout(() => this.renameInput?.nativeElement.focus());
  }

  cancelRenaming() {
    this.renamingRowId = null;
    this.renamingRowKind = null;
    this.renamingDirPath = null;
    this.renameDraft = '';
  }

  submitRename(r: Row) {
    const newName = this.renameDraft.trim();
    if (!newName || newName === r.name) {
      this.cancelRenaming();
      return;
    }

    if (r.kind === 'file') {
      this.renameRequested.emit({
        documentId: r.id,
        newFilename: newName,
      });
    } else if (r.kind === 'dir' && this.renamingDirPath) {
      this.renameDirectoryRequested.emit({
        path: this.renamingDirPath,
        newName,
      });
    }

    this.cancelRenaming();
  }

  onRenameInput(ev: Event) {
    this.renameDraft = (ev.target as HTMLInputElement).value;
  }

  onRenameKeydown(ev: KeyboardEvent) {
    if (ev.key === 'Enter') {
      const row = this.rows.find(x => x.id === this.renamingRowId);
      if (row) this.submitRename(row);
    } else if (ev.key === 'Escape') {
      this.cancelRenaming();
    }
  }

  onDragOverWindow(ev: DragEvent) {
    ev.preventDefault();
  }

  onDragEnterContainer(ev: DragEvent) {
    ev.preventDefault();
    this.dragCounter++;
    this.isDragActive = true;
  }

  onDragLeaveContainer(ev: DragEvent) {
    ev.preventDefault();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.isDragActive = false;
      this.dragCounter = 0;
    }
  }

  onDropContainer(ev: DragEvent) {
    ev.preventDefault();
    this.isDragActive = false;
    this.dragCounter = 0;
    const files = ev.dataTransfer?.files;
    if (files && files.length > 0) {
      this.queueFiles(Array.from(files));
    }
  }

  onDocumentDragStart(ev: DragEvent, docId: string) {
    this.draggedDocumentId = docId;
    ev.dataTransfer?.setData('text/plain', docId);
  }

  onDocumentDragEnd() {
    this.draggedDocumentId = null;
    this.activeDropDirId = null;
  }

  onFileDragStart(ev: DragEvent, r: Row) {
    if (r.kind === 'file') {
      this.onDocumentDragStart(ev, r.id);
    }
  }

  onFileDragEnd() {
    this.onDocumentDragEnd();
  }

  onDirDragEnter(ev: DragEvent, dir: DirRow) {
    if (!this.draggedDocumentId) return;
    ev.preventDefault();
    this.activeDropDirId = dir.id;
  }

  onDirDragOver(ev: DragEvent, dir: DirRow) {
    if (!this.draggedDocumentId) return;
    ev.preventDefault();
    this.activeDropDirId = dir.id;
  }

  onDirDragLeave(ev: DragEvent, dir: DirRow) {
    if (this.activeDropDirId === dir.id) {
      this.activeDropDirId = null;
    }
  }

  onDirDrop(ev: DragEvent, dir: DirRow) {
    ev.preventDefault();
    const docId = this.draggedDocumentId || ev.dataTransfer?.getData('text/plain');
    this.activeDropDirId = null;
    this.draggedDocumentId = null;
    if (!docId) return;

    this.pendingDropDir = dir;
  }

  confirmPendingDrop() {
    if (!this.pendingDropDir) return;
    const docId = this.draggedDocumentId || '';
    if (docId) {
      this.moveDocumentRequested.emit({ documentId: docId, dirPath: this.pendingDropDir.path });
    }
    this.pendingDropDir = null;
  }

  cancelPendingDrop() {
    this.pendingDropDir = null;
  }

  goRoot(): void {
    this.navigate.emit(this.rootPath || '');
  }

  goToCrumb(index: number): void {
    const item = this.breadcrumbDisplayData[index];
    if (item) {
      this.navigate.emit(item.path);
    }
  }

  onBreadcrumbDragEnter(ev: DragEvent, path: string, bIndex: number | null) {
    if (!this.draggedDocumentId) return;
    ev.preventDefault();
    this.activeBreadcrumbIndex = bIndex;
  }

  onBreadcrumbDragOver(ev: DragEvent, path: string, bIndex: number | null) {
    if (!this.draggedDocumentId) return;
    ev.preventDefault();
    this.activeBreadcrumbIndex = bIndex;
  }

  onBreadcrumbDragLeave(ev: DragEvent, bIndex: number | null) {
    if (this.activeBreadcrumbIndex === bIndex) {
      this.activeBreadcrumbIndex = null;
    }
  }

  onBreadcrumbDrop(ev: DragEvent, path: string, bIndex: number | null) {
    ev.preventDefault();
    const docId = this.draggedDocumentId || ev.dataTransfer?.getData('text/plain');
    this.activeBreadcrumbIndex = null;
    this.draggedDocumentId = null;
    if (!docId) return;

    this.pendingBreadcrumbPath = path;
  }

  confirmBreadcrumbDrop() {
    if (!this.pendingBreadcrumbPath) return;
    const docId = this.draggedDocumentId || '';
    if (docId) {
      this.moveDocumentRequested.emit({ documentId: docId, dirPath: this.pendingBreadcrumbPath });
    }
    this.pendingBreadcrumbPath = null;
  }

  cancelBreadcrumbDrop() {
    this.pendingBreadcrumbPath = null;
  }

  toggleActionsMenu(ev?: MouseEvent) {
    if (ev) ev.stopPropagation();
    this.actionsMenuOpen = !this.actionsMenuOpen;
  }

  openDropdownOption(action: 'upload' | 'folder'): void {
    this.actionsMenuOpen = false;
    if (action === 'upload') {
      this.triggerUpload();
    } else if (action === 'folder') {
      this.openCreateFolderInput();
    }
  }

  closeActionsMenu() {
    this.actionsMenuOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent) {
    if (this.actionsMenuOpen) {
      const target = ev.target as Node;
      const btn = this.actionButton?.nativeElement;
      const dd = this.dropdown?.nativeElement;
      if (btn?.contains(target) || dd?.contains(target)) return;
      this.actionsMenuOpen = false;
    }
  }

  // helpers
  isDir(r: Row): r is DirRow {
    return r.kind === 'dir';
  }

  isFile(r: Row): r is Extract<Row, { kind: 'file' }> {
    return r.kind === 'file';
  }

  fmtDate(ts?: number): string {
    if (!ts) return '—';
    const ms = ts < 1e11 ? ts * 1000 : ts;
    return new Date(ms).toLocaleString();
  }

  humanSize(b: number): string {
    if (!b) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(b) / Math.log(1024));
    return `${(b / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  }

  buildBreadcrumbDisplayData(): BreadcrumbItem[] {
    const rawSegments = this.currentPath ? this.currentPath.split('.') : [];
    const fullPathSoFar: string[] = [];
    const items: BreadcrumbItem[] = [];

    rawSegments.forEach((segment, idx) => {
      fullPathSoFar.push(segment);

      if (segment === 'patients' && idx === 0) return;
      if (rawSegments[0] === 'patients' && idx === 1) return;

      if (segment === 'private' || segment === 'shared') {
        const path = fullPathSoFar.join('.');
        if (segment === 'shared') {
          items.push({ label: 'Shared with Care Team', path, sourceIndex: idx });
        } else {
          items.push({ label: 'Private Folder', path, sourceIndex: idx });
        }
        return;
      }

      if (idx === 3 && rawSegments[2] === 'private') return;

      items.push({
        label: segment,
        path: fullPathSoFar.join('.'),
        sourceIndex: idx
      });
    });

    return items;
  }

  private queueFiles(files: File[]) {
    if (this.uploadsDisabled) return;
    this.pendingUploads = files.map(f => ({ name: f.name, size: f.size }));
    const currentDir = this.currentPath || this.rootPath;
    files.forEach(file => {
      this.uploadRequested.emit({ file, dirPath: currentDir });
    });
  }

  private clearPendingUploads() {
    this.pendingUploads = [];
  }
}
